import { useSelector } from 'react-redux';
import { AppColors } from 'common/src/types';
import { createContext, useContext, useMemo, useState, useEffect, ReactNode } from 'react';

export type ThemeType = 'light' | 'dark';

export interface ColorTypes extends AppColors { }

export interface ThemeContextType {
    theme: ThemeType;
    colors: ColorTypes;
    isSystemTheme: boolean;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
    children: ReactNode;
}

// const getEnvColors = () => {
//     return {
//         light: {
//             star: process.env.REACT_APP_COLOR_LIGHT_STAR!,
//             card: process.env.REACT_APP_COLOR_LIGHT_CARD!,
//             text: process.env.REACT_APP_COLOR_LIGHT_TEXT!,
//             white: process.env.REACT_APP_COLOR_LIGHT_WHITE!,
//             black: process.env.REACT_APP_COLOR_LIGHT_BLACK!,
//             border: process.env.REACT_APP_COLOR_LIGHT_BORDER!,
//             danger: process.env.REACT_APP_COLOR_LIGHT_DANGER!,
//             shadow: process.env.REACT_APP_COLOR_LIGHT_SHADOW!,
//             primary: process.env.REACT_APP_COLOR_LIGHT_PRIMARY!,
//             success: process.env.REACT_APP_COLOR_LIGHT_SUCCESS!,
//             polyline: process.env.REACT_APP_COLOR_LIGHT_POLYLINE!,
//             background: process.env.REACT_APP_COLOR_LIGHT_BACKGROUND!,
//             waterColor: process.env.REACT_APP_COLOR_LIGHT_WATER_COLOR!,
//             placeholder: process.env.REACT_APP_COLOR_LIGHT_PLACEHOLDER!,
//             transparent: process.env.REACT_APP_COLOR_LIGHT_TRANSPARENT!,
//             notification: process.env.REACT_APP_COLOR_LIGHT_NOTIFICATION!,
//         }!,
//         dark: {
//             star: process.env.REACT_APP_COLOR_DARK_STAR!,
//             card: process.env.REACT_APP_COLOR_DARK_CARD!,
//             text: process.env.REACT_APP_COLOR_DARK_TEXT!,
//             white: process.env.REACT_APP_COLOR_DARK_WHITE!,
//             black: process.env.REACT_APP_COLOR_DARK_BLACK!,
//             border: process.env.REACT_APP_COLOR_DARK_BORDER!,
//             shadow: process.env.REACT_APP_COLOR_DARK_SHADOW!,
//             danger: process.env.REACT_APP_COLOR_DARK_DANGER!,
//             primary: process.env.REACT_APP_COLOR_DARK_PRIMARY!,
//             success: process.env.REACT_APP_COLOR_DARK_SUCCESS!,
//             polyline: process.env.REACT_APP_COLOR_DARK_POLYLINE!,
//             background: process.env.REACT_APP_COLOR_DARK_BACKGROUND!,
//             waterColor: process.env.REACT_APP_COLOR_DARK_WATER_COLOR!,
//             transparent: process.env.REACT_APP_COLOR_DARK_TRANSPARENT!,
//             placeholder: process.env.REACT_APP_COLOR_DARK_PLACEHOLDER!,
//             notification: process.env.REACT_APP_COLOR_DARK_NOTIFICATION!,
//         },
//     };
// };

const getEnvColors = () => {
    return {
        light: {
            star: "#EAC32D",
            card: "#252525",
            text: "#ffffff",
            white: "#ffffff",
            black: "#000000",
            border: "#404040",
            danger: "#ff6b6b",
            success: "#51cf66",
            polyline: "#dc3545",
            background: "#1a1a1a",
            waterColor: "#0077be",
            placeholder: "#8a8a8a",
            transparent: "transparent",
            notification: "#ff6b6b",
            shadow: "rgba(255, 255, 255, 0.05)",
            primary: process.env.REACT_APP_COLOR_ADM!,
        },
        dark: {
            star: "#EAC32D",
            card: "#252525",
            text: "#ffffff",
            white: "#ffffff",
            black: "#000000",
            border: "#404040",
            danger: "#ff6b6b",
            success: "#51cf66",
            polyline: "#dc3545",
            background: "#1a1a1a",
            waterColor: "#0077be",
            placeholder: "#8a8a8a",
            transparent: "transparent",
            notification: "#ff6b6b",
            shadow: "rgba(255, 255, 255, 0.05)",
            primary: process.env.REACT_APP_COLOR_ADM!,
        },
    };
};

export function ThemeProviderApp({ children }: ThemeProviderProps) {
    const settings = useSelector((state) => state.settingsdata?.settings);

    const [isSystemTheme, setIsSystemTheme] = useState(true);
    const [theme, setTheme] = useState<ThemeType>(() => {
        if (typeof window !== 'undefined') {
            const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            return isDark ? 'dark' : 'light';
        }
        return 'light';
    });


    const DefaultColors = useMemo(() => getEnvColors(), []);

    const CustomLightTheme: ColorTypes = useMemo(
        () => {
            return ({
                ...DefaultColors.light,
                ...settings?.theme?.light
            });
        },
        [DefaultColors.light, settings?.theme?.light]
    );

    const CustomDarkTheme: ColorTypes = useMemo(
        () => ({
            ...DefaultColors.dark,
            ...settings?.theme?.dark
        }),
        [DefaultColors.dark, settings?.theme?.dark]
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const handleChange = (e: MediaQueryListEvent) => {
            if (isSystemTheme) {
                setTheme(e.matches ? 'dark' : 'light');
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [isSystemTheme]);

    useEffect(() => {
        if (isSystemTheme && typeof window !== 'undefined') {
            const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            setTheme(isDark ? 'dark' : 'light');
        }
    }, [isSystemTheme]);

    const toggleTheme = () => {
        if (isSystemTheme) {
            setIsSystemTheme(false);
            const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            setTheme(isDark ? 'light' : 'dark');
        } else {
            setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
        }
    };

    const currentTheme = useMemo(
        () => (isSystemTheme ? theme : theme),
        [isSystemTheme, theme]
    );

    const colors: ColorTypes = useMemo(
        () => (currentTheme === 'dark' ? CustomDarkTheme : CustomLightTheme),
        [currentTheme, CustomDarkTheme, CustomLightTheme]
    );

    useEffect(() => {
        if (typeof document !== 'undefined' && colors) {
            Object.entries(colors).forEach(([key, value]) => {
                document.documentElement.style.setProperty(`--color-${key.toLowerCase()}`, value);
            });

            document.body.style.backgroundColor = colors.background || '';
        }
    }, [colors]);

    const themeValue = useMemo(
        () => ({
            theme: currentTheme,
            isSystemTheme,
            toggleTheme,
            colors,
        }),
        [currentTheme, isSystemTheme, colors]
    );

    return (
        <ThemeContext.Provider value={themeValue}>{children}</ThemeContext.Provider>
    );
}

export function useThemeApp(): ThemeContextType {
    const context = useContext(ThemeContext);
    if (!context)
        throw new Error("useThemeApp must be used within a ThemeProviderApp");
    return context;
}